import { useCallback, useContext, useEffect, useMemo, useState, type JSX } from 'react';

import { Form, StepContainer, FormElement } from '@/components/old';

import { config } from '@/config/index';

import { convertToArrayBuffer } from '@/helpers/converters';
import { ServiceFileManager } from '@/helpers/serviceFileManager';

import { ServicesContext } from '@/contexts/Services/ServicesContext';

import { SERVICE_CODES } from '@/lib/consts';

import { ERZPermit, FormDataType, UserService } from '@/api/types';

import api from '@/api';

import { HomeButton, NextStepButton, SignButton } from '../../../../../../components/Forms/Buttons';
import { getServiceData } from '../../../../../../components/Forms/constants';
import { Loader } from '@/shared/ui';
import AuthContext from '../../../../../../contexts/Auth/AuthContext';
import { ServiceAvailabilityProvider } from '../../../../../../contexts/ServiceAvailability/ServiceAvailabilityContext';
import { currentServiceCode, stepNumberToSubmitForm, totalSteps } from '../CancellationTemporaryResidenceRelocation';

import Step1 from './Step1';
import Step2 from './Step2';
import Step3 from './Step3';

type ServiceDataType = Pick<UserService, 'id' | 'name' | 'code'>;

export interface StepProps {
  formData: FormDataType;
  handleInputChange: (name: string, value: unknown) => void;
}

type Props = {
  onReceiveRequestId: (requestId: string) => void;
};

const FormSteps: React.FC<Props> = ({ onReceiveRequestId }) => {
  const { userData } = useContext(AuthContext);
  const { getServiceByCode } = useContext(ServicesContext);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [currentRequests, setCurrentRequests] = useState<ERZPermit[]>([]);
  const [isDisabled, setIsDisabled] = useState<boolean>(!config.signUp.testFlow);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [serviceData, setServiceData] = useState<ServiceDataType>();
  const serviceFileManager = useMemo(() => new ServiceFileManager(), []);
  const signUpPayload = useMemo(() => convertToArrayBuffer(currentServiceCode), []);

  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      api.permits.getAll({ permitType: SERVICE_CODES.TEMPORARY_RESIDENCE_RELOCATION }),
      getServiceData(currentServiceCode, getServiceByCode(currentServiceCode)),
    ])
      .then(([{ data: permits }, { service }]) => {
        setCurrentRequests(permits);
        onReceiveRequestId(permits[0].id);
        setServiceData(service);
      })
      .finally(() => setIsLoading(false));
  }, []);

  const scrollTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  const handleSignUp = useCallback(() => {
    setIsDisabled(false);
  }, []);

  const handleNextStep = async (): Promise<void> => {
    scrollTop();
    setCurrentStep((prevStep) => prevStep + 1);
  };

  const StepButtons = function (): JSX.Element {
    let containerBtn = null;

    if (currentStep === 1) {
      containerBtn = <NextStepButton handleNextStep={handleNextStep} disabled={!currentRequests.length} />;
    } else if (currentStep === stepNumberToSubmitForm) {
      containerBtn = (
        <SignButton
          disabled={isDisabled || isLoading}
          onClick={(): void => {
            setIsLoading(true);
            api.services
              .submit(currentServiceCode, {
                ...serviceFileManager.getSignature(),
                // request id of IAC02
                cancelledRequestId: currentRequests[0].id,
                // for back XML
                permitType: SERVICE_CODES.TEMPORARY_RESIDENCE_RELOCATION,
                service: serviceData,
              })
              .then(() => {
                setCurrentStep((prevStep) => prevStep + 1);
              })
              .catch((error) => {
                // eslint-disable-next-line no-console
                console.error(error);
              })
              .finally(() => setIsLoading(false));
          }}
        />
      );
    } else {
      containerBtn = <HomeButton />;
    }

    return !!containerBtn && <FormElement variant="bottom">{containerBtn}</FormElement>;
  };

  const renderFormStep = (step: number): Nullable<JSX.Element> => {
    switch (step) {
      case 1:
        return <Step1 currentRequests={currentRequests} />;

      case 2:
        return (
          <Step2
            fileManager={serviceFileManager}
            rnokpp={userData?.drfo_code}
            onSignComplete={handleSignUp}
            legacySignUpPayload={signUpPayload}
          />
        );

      case 3:
        return <Step3 serviceCode={currentServiceCode} />;

      default:
        return null;
    }
  };

  return (
    <Form name="cancellationTemporaryResidenceRelocation" className="max-width-md" isBgDefault={true}>
      <StepContainer isLoading={isLoading} data-step={currentStep}>
        <ServiceAvailabilityProvider
          serviceCode={currentServiceCode}
          skipAvailabilityValidation={currentStep === totalSteps}
        >
          {isLoading ? (
            <Loader centered />
          ) : (
            <>
              {renderFormStep(currentStep)}
              {StepButtons()}
            </>
          )}
        </ServiceAvailabilityProvider>
      </StepContainer>
    </Form>
  );
};

export default FormSteps;
